import { CHRONICLE_TYPE_LABELS, type ChronicleEntry } from '@brimstone/shared';
import { EntryPhotos } from './Photos';
import { ShowOnMap } from './ShowOnMap';

/** "Friday 20 November", in local time. */
function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

function time(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/** Entries grouped by local day, newest first as given. */
function byDay(entries: ChronicleEntry[]): { day: string; entries: ChronicleEntry[] }[] {
  const out: { day: string; entries: ChronicleEntry[] }[] = [];
  for (const e of entries) {
    const day = dayLabel(e.occurredAt);
    const last = out.at(-1);
    if (last?.day === day) last.entries.push(e);
    else out.push({ day, entries: [e] });
  }
  return out;
}

/**
 * The Chronicle as a journal: a header per day, then each entry's time and text, and the
 * screenshots taken of it (M12). Times derived from a snapshot diff read "around", since they are
 * the logout the change was seen at.
 */
export function ChronicleFeed({ entries }: { entries: ChronicleEntry[] }) {
  return (
    <div className="flex flex-col">
      {byDay(entries).map(({ day, entries: list }) => (
        <section key={day} aria-label={day}>
          <h3 className="m-0 flex justify-between gap-3 border-b border-line bg-bg px-4 py-2 text-label font-normal uppercase tracking-label text-muted">
            <span>{day}</span>
            <span className="font-mono">{list.length}</span>
          </h3>
          <ol className="m-0 list-none p-0">
            {list.map((e) => (
              <li
                key={e.id}
                className="grid grid-cols-[104px_minmax(0,1fr)] items-baseline gap-x-3 border-b border-line-soft px-4 py-2.5 last:border-b-0 sm:grid-cols-[104px_minmax(0,1fr)_auto]"
              >
                <span className="font-mono text-secondary text-muted">
                  {e.approx ? `around ${time(e.occurredAt)}` : time(e.occurredAt)}
                </span>
                <span>
                  {e.text}
                  {e.map && (
                    <>
                      {' '}
                      <ShowOnMap link={e.map} character={e.characterId} />
                    </>
                  )}
                  <EntryPhotos entry={e} />
                </span>
                <span className="hidden font-mono text-label uppercase text-muted sm:inline">
                  {CHRONICLE_TYPE_LABELS[e.type]}
                </span>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}
